<template>
  <v-dialog
    v-model="isVisible"
    :fullscreen="isFullScreen"
    height="64"
    max-height="64"
    max-width="850"
    persistent
    scrollable
  >
    <v-card v-if="editItem">
      <v-toolbar
        flat
      >
        <v-avatar
          class="mr-3"
          color="blue"
        >
          <v-icon color="blue lighten-5">
            {{ editItem.Id ? $t('Marketing.Print.Dialog.EditTemplate.Toolbar.Icon') : $t('Marketing.Print.Dialog.AddTemplate.Toolbar.Icon') }}
          </v-icon>
        </v-avatar>


        <v-toolbar-title
          class="pl-1"
        >
          <div class="body-3">
            {{ editItem.Id ? $t('Marketing.Print.Dialog.EditTemplate.Toolbar.Title') : $t('Marketing.Print.Dialog.AddTemplate.Toolbar.Title') }}
            <span v-if="editItem.Title">({{ editItem.Title }})</span>
          </div>
          <div class="text-caption">
            {{ editItem.Id ? $t('Marketing.Print.Dialog.EditTemplate.Toolbar.SubTitle') : $t('Marketing.Print.Dialog.AddTemplate.Toolbar.SubTitle') }}
          </div>
        </v-toolbar-title>

        <v-spacer />

        <v-btn
          class="ml-0"
          icon
          @click="onCancelButtonClick"
        >
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-tabs
        v-model="activeTab"
        :scrollable="false"
        background-color="indigo"
        dark
        slider-color="light-green"
      >
        <v-tab
          key="tab1"
          active-class="v-tabs__item--active indigo darken-2"
          href="#tab1"
        >
          {{ $t('Marketing.Print.Dialog.AddTemplate.Toolbar.Title').toLocaleUpperCase($i18n.locale) }}
        </v-tab>

        <v-tab
          key="tab2"
          active-class="v-tabs__item--active indigo darken-2"
          href="#tab2"
        >
          {{ $t('Restaurant.OptionAddDialog.Tab.Values').toLocaleUpperCase($i18n.locale) }}
        </v-tab>
      </v-tabs>

      <v-divider />

      <v-card-text class="pa-2 fill-height">
        <v-form
          ref="dataForm"
          v-model="dataFormValid"
          @submit.prevent
        >
          <v-tabs-items v-model="activeTab">
            <v-tab-item
              key="tab1"
              value="tab1"
            >
              <v-container
                class="pa-0"
                fluid
              >
                <v-row dense>
                  <v-col cols="12">
                    <v-text-field
                      v-model="editItem.Title"
                      :hint="$t('Marketing.Print.Dialog.EditTemplate.Field.Title.Hint')"
                      :label="$t('Marketing.Print.Dialog.EditTemplate.Field.Title.Label')"
                      :prepend-icon="$t('Marketing.Print.Dialog.EditTemplate.Field.Title.Icon')"
                      :rules="formFieldRules.Title"
                      filled
                      hide-details="auto"
                    />
                  </v-col>
                </v-row>

                <v-row
                  v-if="isVisible"
                  dense
                >
                  <v-col cols="12">
                    <unlayer-editor
                      ref="editor"
                      :appearance="editor.appearance"
                      :editor-id="editor.id"
                      :min-height="editor.minHeight"
                      :options="editor.options"
                      :project-id="editor.projectId"
                      :tools="editor.tools"
                      @load="editorLoaded"
                      @ready="editorReady"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>

            <v-tab-item
              key="tab2"
              value="tab2"
            >
              <v-container
                class="pa-1"
                fluid
              >
                <v-row dense>
                  <v-col cols="12">
                    <v-simple-table>
                      <thead>
                        <tr>
                          <th class="text-center pa-1">
                            {{ $t('Restaurant.MenuAddDialog.Field.SelectOption.ListView.Header.Name') }}
                          </th>

                          <th class="text-center pa-1">
                            {{ $t('Restaurant.MenuAddDialog.Field.SelectOption.ListView.Header.Price') }}
                          </th>

                          <th
                            class="text-center pa-1"
                            style="width: 70px;"
                          >
                            <v-btn
                              class="primary white--text"
                              depressed
                              icon
                              x-small
                              @click="addFieldItem"
                            >
                              <v-icon>mdi-plus</v-icon>
                            </v-btn>
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        <template v-for="(fieldItem, fieldIdx) in template.Fields">
                          <tr :key="`template-field-${fieldIdx}`">
                            <td class="pa-2 text-center">
                              <v-text-field
                                v-model="fieldItem.Key"
                                dense
                                filled
                                hide-details
                                label="Key"
                                single-line
                              />
                            </td>

                            <td class="pa-2 text-center">
                              <v-text-field
                                v-model="fieldItem.Value"
                                dense
                                filled
                                hide-details
                                label="Value"
                                single-line
                              />
                            </td>

                            <td class="pa-1 text-center">
                              <v-btn
                                :disabled="!userCanManage"
                                class="red white--text"
                                depressed
                                icon
                                x-small
                                @click="removeFieldItem(fieldIdx)"
                              >
                                <v-icon>mdi-close</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </v-simple-table>
                  </v-col>
                </v-row>
              </v-container>
            </v-tab-item>
          </v-tabs-items>
        </v-form>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-2">
        <v-spacer />

        <v-btn
          class="light-blue--text text--darken-1"
          outlined
          @click="onCancelButtonClick"
        >
          {{ $t('Common.Button.Close').toLocaleUpperCase($i18n.locale) }}
        </v-btn>

        <v-btn
          :disabled="formIsSaving || !editorIsReady"
          :loading="formIsSaving"
          color="green"
          dark
          depressed
          @click="onSaveButtonClick"
        >
          {{ $t('Common.Button.Save').toLocaleUpperCase($i18n.locale) }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import Auth from '@/mixins/auth'
import DialogVisible from '@/mixins/dialogVisible'
import {clone} from '@/lib/helper/helper.js'
import UnlayerEditor from '@/components/common/unlayer-editor/UnlayerEditor.vue'

export default {
  name      : 'PrintTemplateEditDialog',
  components: {UnlayerEditor},
  mixins    : [Auth, DialogVisible],
  props     : {
    template: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      activeTab     : 'tab1',
      isFullScreen  : true,
      dataFormValid : false,
      editItem      : null,
      defaultDataObj: {
        Id      : null,
        Title   : '',
        Subject : '',
        Body    : '',
        BodyText: '',
        Source  : '',
        Type    : 'print'
      },
      editorIsReady: false,
      editor       : {
        id       : 'print-template-editor-id',
        //projectId: 0,
        locale   : 'en-US',
        minHeight: '745px',
        tools    : {},
        options  : {
          version    : 'stable', // Specify 'latest', 'stable' or a specific version number
          displayMode: 'document', // email, web, popup, document
          features   : {
            textEditor: {
              tables: true, // Enable tables in the text editor
              emojis: true // Enable emoji support in the text editor
            }
          },
          mergeTags: {
            customer: {
              name     : 'Customer',
              mergeTags: {
                first_name: {
                  name  : 'First Name',
                  value : '{{first_name}}',
                  sample: 'John'
                },
                last_name: {
                  name  : 'Last Name',
                  value : '{{last_name}}',
                  sample: 'Doe'
                },
                email: {
                  name  : 'Email',
                  value : '{{email}}',
                  sample: 'test@test.com'
                }
              }
            }
          }
        }
      },
      formIsSaving  : false,
      formFieldRules: {
        Title: [
          (v) => !!v || this.$t('Marketing.Print.Dialog.EditTemplate.Field.Title.Error.Required'),
          (v) => v && v.length >= 2 && v.length <= 255 || this.$t('Marketing.Print.Dialog.EditTemplate.Field.Title.Error.Between')
        ],
        Subject: [
          (v) => !!v || this.$t('Marketing.Print.Dialog.EditTemplate.Field.Subject.Error.Required'),
          (v) => v && v.length >= 2 && v.length <= 255 || this.$t('Marketing.Print.Dialog.EditTemplate.Field.Subject.Error.Between')
        ]
      }
    }
  },
  computed: {},
  watch   : {
    isVisible(newVal) {
      if (newVal) {
        this.initDialog()
      } else {
        this.destroyDialog()
      }
    }
  },
  created() {
    this.initDialog()
  },
  mounted() {
  },
  updated() {
  },
  beforeDestroy() {
    this.destroyDialog()
  },
  methods: {
    initDialog() {
      this.activeTab = 'tab1'
      this.editItem = clone(this.template || this.defaultDataObj)
      this.editorIsReady = false
      this.initEvents()
    },
    destroyDialog() {
      this.activeTab = 'tab1'
      this.editItem = null
      this.editorIsReady = false
      this.destroyEvents()
    },
    initEvents() {
      this.destroyEvents()
      this.$bus.$on(window.SocketCommand.Marketing.Print.Save, this.onSaveResult)
    },
    destroyEvents() {
      this.$bus.$off(window.SocketCommand.Marketing.Print.Save, this.onSaveResult)
    },

    editorReady() {
      this.editorIsReady = true
    },

    editorLoaded() {
      if (this.editItem?.Source) {
        this.$refs.editor.editor.loadDesign(this.editItem.Source)
      }
    },

    addFieldItem() {
      this.template.Fields.unshift({Key: '', Value: ''})
    },
    removeFieldItem(idx) {
      this.template.Fields.splice(idx, 1)
    },

    onSaveButtonClick() {
      if (!this.$refs.dataForm.validate()) return

      this.formIsSaving = true
      const payload = clone(this.editItem)

      payload.Fields = payload.Fields.filter(field => !!field.Key)

      this.$refs.editor.editor.exportHtml(
        (data) => {
          payload.Source = data?.design || null
          //payload.Chunks = data?.chunks || null
          payload.Fields = this.editItem?.Fields || null
          payload.Body = this.htmlDecode(data?.html || '')

          window.callAS(window.SocketCommand.Marketing.Print.Save, payload)
        },
        {
          cleanup     : true,
          inlineStyles: true,
          minify      : true
        }
      )
    },

    onSaveResult(data) {
      this.formIsSaving = false

      if (data && data.status === 'success') {
        if (data?.Id) this.editItem.Id = data.Id
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Marketing.Print.Dialog.EditTemplate.Message.Save.Success'),
          type: 'success',
          icon: 'info'
        })
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Error.Generic'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    htmlDecode(input) {
      const doc = new DOMParser().parseFromString(input, 'text/html')
      return doc.querySelector('body').innerHTML
    },

    onCancelButtonClick() {
      this.closeDialog()
    }
  }
}
</script>
